import { useEffect, useState } from 'react';
import { Ability, CommonAbilityService, CommonSectorService, CommonSkillService, Option, RootState, ShortUser, Skill, UUID } from '@umanforge/common';
import { UserService } from 'services/users.service';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import { GlobalService } from 'services/global.service';
import { useDispatch, useSelector } from 'react-redux';
import { RoleTaskService } from 'services/roleTask.service';
import { SkillRT } from 'interfaces/roleTaskSkill.interface';
import { useTranslation } from 'react-i18next';

export interface ErrorRoleTask {
  users: string | undefined;
  count: string | undefined;
  equipmentAbilities: string | undefined;
  equipmentSkills: string | undefined;
};

export function useRoleTaskForm(
  sectorId: UUID,
  taskId: UUID | undefined,
  onChange: ( roleTasks: RoleTaskFull[], hasChanged: boolean, isValid: boolean, requiredAbilities: string[], requiredSkills: { id: string; iluo: number | undefined }[] ) => void,
  equipmentSkills: Option<Skill>[],
  equipmentAbilities: Option<Ability>[],
) {

  const defaultRoleTask: RoleTaskFull = {
    abilities: [],
    skills: [],
    users: [],
    id: '',
    idTask: '',
    count: 1
  };

  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );

  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ sectorUsers, setSectorUsers ] = useState<ShortUser[]>( [] );
  const [ allowedSkills, setAllowedSkills ] = useState<SkillRT[]>( [] );
  const [ allowedAbilities, setAllowedAbilities ] = useState<Ability[]>( [] );

  const [ localRoleTasks, setLocalRoleTasks ] = useState<RoleTaskFull[]>( taskId ? [] : [ { ...defaultRoleTask } ] );
  const [ roleTasksSave, setRoleTasksSave ] = useState<RoleTaskFull[]>( taskId ? [] : [ { ...defaultRoleTask } ] );
  const [ errors, setErrors ] = useState<ErrorRoleTask>( );


  const [ isAdmin, setIsAdmin ] = useState<boolean>( false );

  const init = async () => {
    const idSite = ( await CommonSectorService.getSector( sectorId, token, dispatch ) ).siteId;
    if ( me ) {
      const admin = GlobalService.isAdminOfSector( me, sectorId );
      setIsAdmin( admin );
    }

    const [
      abilitiesBySite,
      skillsBySite,
      users,
    ] = await Promise.all( [
      CommonAbilityService.getAllowedAbilitiesBySitesId( [ idSite ], token, dispatch ),
      CommonSkillService.getAllowedSkillsBySitesId( [ idSite ], token, dispatch ),
      UserService.getUsersOfSector( sectorId, token, dispatch ),
    ] );

    if ( taskId ) {
      const roleTasksRes = await RoleTaskService.getRoleTasksFullByTaskId( taskId, token, dispatch );
      if ( roleTasksRes ) {
        setLocalRoleTasks( roleTasksRes );
        setRoleTasksSave( structuredClone( roleTasksRes ) );
      }
    }

    setSectorUsers( users );
    setAllowedAbilities( abilitiesBySite );
    setAllowedSkills( skillsBySite.map( skill => { return { ...skill, iluo: 1 }; } ) );

    setIsFirstRender( false );
  };

  const updateRoleTask = ( newRoleTasks: RoleTaskFull[], forceUpdate: boolean = false ) => {
    const changed = compareRoleTaskList( newRoleTasks, roleTasksSave );
    if ( changed || forceUpdate || localRoleTasks.length == 0 ) {
      setLocalRoleTasks( newRoleTasks );
      const { requiredEquipmentAbilities, requiredEquipmentSkills } = compareRoleTaskEquipmentList( newRoleTasks );
      onChange(
        newRoleTasks,
        changed,
        RoleTaskService.isRoleTaskValid( newRoleTasks ),
        requiredEquipmentAbilities, requiredEquipmentSkills,
      );
    }
  };

  const compareRoleTaskEquipmentList = ( roleTasks: RoleTaskFull[] ) => {
    if ( roleTasks.length == 0 ) {
      return {
        requiredEquipmentAbilities: [],
        requiredEquipmentSkills: []
      };
    }
    const localAbl = [ ...new Set( roleTasks.map( rt => { return rt.abilities.map( abl => { return abl.id;} ); } ).flat() ) ];
    const localSkill = [ ...new Set( roleTasks.map( rt => { return rt.skills.map( skl => { return { id: skl.id, iluo: skl.iluo };} ); } ).flat() ) ];
    const requAbilities: string[] = [];
    const requSkills: { id: string; iluo: number | undefined }[] = [];

    equipmentAbilities.forEach( ea => {
      if ( !localAbl.includes( ea.object.id ) ) {
        requAbilities.push( ea.object.id );
      }
    } );
    equipmentSkills.forEach( es => {
      if ( !localSkill.some( skl => { return skl.id == es.object.id && skl.iluo >= ( es.object.value ?? 1 ); } ) ) {
        requSkills.push( { id: es.object.id, iluo: es.object.value } );
      }
    } );
    setErrors( {
      users: !RoleTaskService.isRoleTaskValid( roleTasks ) ? t( 'translation:errors.role_task_not_valid' ) : undefined,
      count: roleTasks.some( rt => { return rt.count < rt.users.length; } ) ? t( 'translation:errors.too_many_user_on_a_role_task' ) : undefined,
      equipmentAbilities: requAbilities.length > 0 ? t( 'translation:errors.missing_authorizations_for_the_equipments' ) : undefined,
      equipmentSkills: requSkills.length > 0 ? t( 'translation:errors.missing_skills_for_the_equipments' ) : undefined,
    } );
    return {
      requiredEquipmentAbilities: requAbilities,
      requiredEquipmentSkills: requSkills
    };
  };

  const handleRoleTaskChange = ( roleTask: RoleTaskFull, index: number, forceUpdate: boolean = false ) => {
    let roleTasksTmp = [ ...localRoleTasks ];
    roleTasksTmp[ index ] = { ...roleTask };
    roleTasksTmp = [ ...roleTasksTmp.filter( rt => { return rt != null;} ) ];
    updateRoleTask( roleTasksTmp, forceUpdate );
  };

  const handleRoleTaskDelete = ( roleTask: RoleTaskFull, index: number ) => {
    let roleTasksTmp = [ ...localRoleTasks ];
    delete roleTasksTmp[ index ];
    roleTasksTmp = roleTasksTmp.filter( rt => { return rt != null;} );
    updateRoleTask( roleTasksTmp, true );
  };

  const handleAddRoleTask = () => {
    let roleTasksTmp = [ {
      abilities: [],
      skills: [],
      users: [],
      id: '',
      idTask: '',
      count: 1
    }, ...localRoleTasks ];
    roleTasksTmp = [ ...roleTasksTmp.filter( rt => { return rt != null;} ) ];
    updateRoleTask( roleTasksTmp );
  };

  // True if different, False if same
  const compareRoleTaskList = ( a: RoleTaskFull[], b: RoleTaskFull[] ): boolean => {
    const compare = ( aa: { id: string }, bb: { id: string } ) => {
      return aa.id > bb.id ? 1 : -1;
    };
    return (
      a.length != b.length ||
        a.sort( compare ).map( rts => {return (
          rts.id + rts.count +
          rts.abilities.sort( compare ).map( ab => {return ab.id;} ) +
          rts.skills.sort( compare ).map( s => {return ( s.id + s.iluo );} ) +
          rts.users.sort( compare ).map( u => {return ( u.id );} )
        );} ).join()
        !=
        b.sort( compare ).map( rts => {return (
          rts.id + rts.count +
          rts.abilities.sort( compare ).map( ab => {return ab.id;} ) +
          rts.skills.sort( compare ).map( s => {return ( s.id + s.iluo );} ) +
          rts.users.sort( compare ).map( u => {return ( u.id );} )
        );} ).join()
    );
  };

  // UseEffects
  useEffect( () => {
    init();
  }, [] );


  useEffect( () => {
    // if its a new task
    if ( !isFirstRender ) {
      if ( !taskId ) {
        if ( equipmentAbilities.length > 0 || equipmentSkills.length > 0 ) {
          let roletask: RoleTaskFull | null = null;
          let roletaskIndex = 0;

          const roleTasksTmp = [ ...localRoleTasks.filter( ( rt, i ) => {
            if ( rt?.id === 'equipment_roletask' ) {
              roletask = rt;
              roletaskIndex = i;
            }
            return rt != null;
          } ) ];

          // modify
          if ( roletask ) {
            const rt = roletask as RoleTaskFull;
            handleRoleTaskChange( {
              abilities: [ ...equipmentAbilities.map( option => { return option.object; } ) ],
              skills: [ ...equipmentSkills.map( option => { return { ...option.object, iluo: option.object.value as number }; } ) ],
              users: rt.users,
              id: 'equipment_roletask',
              idTask: '',
              count: rt.count,
              isValid: rt.isValid
            }, roletaskIndex );
          }

          // add
          else {
            roleTasksTmp.push( {
              abilities: [ ...equipmentAbilities.map( option => { return option.object; } ) ],
              skills: [ ...equipmentSkills.map( option => { return { ...option.object, iluo: option.object.value as number }; } ) ],
              users: [],
              id: 'equipment_roletask',
              idTask: '',
              count: 1
            } );

            updateRoleTask( roleTasksTmp );
          }
        }

        // delete
        else {
          localRoleTasks.forEach( ( rt, i ) => {
            if ( rt?.id === 'equipment_roletask' ) {
              handleRoleTaskDelete( rt, i );
            }
          } );
        }
      }
      else {
        const { requiredEquipmentAbilities, requiredEquipmentSkills } = compareRoleTaskEquipmentList( localRoleTasks );
        onChange(
          localRoleTasks,
          false,
          RoleTaskService.isRoleTaskValid( localRoleTasks ),
          requiredEquipmentAbilities, requiredEquipmentSkills
        );
      }
    }
  }, [ equipmentAbilities, equipmentSkills ] );


  return {
    handleAddRoleTask,
    handleRoleTaskDelete,
    handleRoleTaskChange,
    roleTasks: localRoleTasks,
    allowedSkills,
    allowedAbilities,
    sectorUsers,
    isAdmin,
    errors
  };
};
